"use client";

import { formatShares } from "@/lib/format";

/**
 * Single outcome card on the TradePanel — the primary action surface.
 * Click selects the side; the highlighted card drives effective price
 * + CTA color in the parent panel. Designed reusable for the multi-
 * outcome variant; keeps mode/outcome/price/sharesHeld as the only
 * binary-shaped inputs.
 *
 * Extracted from TradePanel.tsx 2026-05-02 — pure presentational, no
 * behaviour change. Co-located with TradePanel under
 * `components/trade/` so future trade-flow components have a clear
 * home.
 */
export type OutcomeCardOutcome = "yes" | "no";
export type OutcomeCardMode = "buy" | "sell";

export function OutcomeCard({
  mode,
  outcome,
  priceCents,
  impliedPriceCents,
  priceLoading,
  selected,
  onClick,
  flashing,
  sharesHeld,
}: {
  mode: OutcomeCardMode;
  outcome: OutcomeCardOutcome;
  priceCents: number;
  // Last-trade / current_price_cents anchor — drives the "X% implied
  // probability" caption regardless of which book side is being
  // quoted, so the implied-probability number stays stable when
  // toggling Buy/Sell.
  impliedPriceCents: number;
  // True while the orderbook fetch hasn't resolved yet. Suppresses
  // the price/caption render so we don't flash the last-trade anchor
  // (e.g. NO=70¢) before flipping to the real best-quote (NO=8¢)
  // when the book is one-sided.
  priceLoading: boolean;
  selected: boolean;
  onClick: () => void;
  flashing: boolean;
  sharesHeld: number;
}) {
  const accent = outcome === "yes" ? "var(--color-yes)" : "var(--color-no)";
  const verb = mode === "buy" ? "BUY" : "SELL";
  const label = `${verb} ${outcome.toUpperCase()}`;
  const impliedPct = impliedPriceCents;
  // Disable SELL card when user holds 0 shares of this outcome —
  // backend rejects with a clean 400, but the pre-emptive disable
  // saves the click + makes the empty state obvious.
  const disabled = mode === "sell" && sharesHeld === 0;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-disabled={disabled}
      className="rounded-md border bg-[var(--color-bg)] px-3 py-2.5 text-left transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        borderColor: selected
          ? accent
          : flashing
            ? accent
            : "var(--color-border)",
        background: selected
          ? `color-mix(in srgb, ${accent} 12%, var(--color-bg))`
          : flashing
            ? `color-mix(in srgb, ${accent} 8%, var(--color-bg))`
            : "var(--color-bg)",
        boxShadow: selected ? `inset 0 0 0 1px ${accent}` : undefined,
      }}
    >
      <div className="flex items-center justify-between">
        <span
          className="text-[10px] font-bold uppercase tracking-[0.4px]"
          style={{ color: accent }}
        >
          {label}
        </span>
        <span aria-hidden className="text-[var(--color-text3)]">
          →
        </span>
      </div>
      <div
        className="mt-1 font-mono text-[20px] font-bold tabular-nums"
        aria-busy={priceLoading || undefined}
      >
        {priceLoading ? (
          <span
            aria-hidden
            className="inline-block h-[24px] w-[60px] animate-pulse rounded"
            style={{ background: "var(--color-border)" }}
          />
        ) : (
          <>{priceCents}¢</>
        )}
      </div>
      {/* Caption: the big number above is the side-aware best quote
          (BUY = ask, SELL = bid). The implied-probability % below is
          the last-trade anchor (stable across mode toggle). Showing
          both labels prevents the "why did my price change when I
          clicked Sell?" confusion the founder reported 2026-04-30.
          While the book is loading we hide the caption — showing
          "X% implied probability" with a skeleton above looks broken. */}
      <div className="mt-0.5 text-[10px] text-[var(--color-text3)]">
        {priceLoading ? (
          <span aria-hidden>&nbsp;</span>
        ) : priceCents !== impliedPriceCents ? (
          <>
            {mode === "buy" ? "best ask" : "best bid"} ·{" "}
            <span title="Last-trade implied probability — stable across Buy/Sell toggle">
              {impliedPct}% implied
            </span>
          </>
        ) : (
          <>{impliedPct}% implied probability</>
        )}
      </div>
      {/* Opposite-side price + spread display moved into the
          TradePanel-level "Order book details" disclosure 2026-05-10.
          Two surfaces showing the same data was the clutter the
          simplification pass was meant to fix. Card now shows
          label + price + implied % only; the spread/bid/ask diagnostic
          lives one expand-click away. */}
      {mode === "sell" ? (
        sharesHeld > 0 ? (
          <div className="mt-1 inline-block rounded bg-black/30 px-1.5 py-0.5 font-mono text-[10px]">
            {/* sharesHeld is centi-shares (post-migration 0145).
                Routed through formatShares per the 2026-05-11 units
                discipline rule — no inline (centi / 100) at render
                sites, where the same pattern produced the 100× value
                regression on DashboardTabs. */}
            {formatShares(sharesHeld)} held
          </div>
        ) : (
          <div className="mt-1 text-[10px] italic text-[var(--color-text3)]">
            0 held — switch to Buy
          </div>
        )
      ) : null}
    </button>
  );
}
